import {
  InfiniteData,
  useQueryClient,
  useInfiniteQuery,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useApiQuery } from "@/core/hooks/useApiQuery";
import { useServices } from "@/core/services/useServices";
import { ReadStoreGroup, WriteStoreGroup } from "@/types/storeGroup";
import { ListOptions } from "./StoreGroupService";
import { ReadStoreGroupMember } from "@/types/storeGroupMember";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { SuccessFetchListResponse } from "@/services/api";
import { Module } from "@/constants";

interface MutationOptions<TData, TVariables>
  extends Omit<
    UseMutationOptions<TData, Error, TVariables, unknown>,
    "mutationFn"
  > {
  errorTitle?: string;
}

type StoreGroupMembersPage = SuccessFetchListResponse<ReadStoreGroupMember>;

type StoreGroupMembersQueryKey = [
  string,
  string,
  ListOptions["filters"] | undefined,
  number | undefined,
  ListOptions["sorting"] | undefined,
];

/**
 * Fetch all store groups globally (for dictionary/select components)
 */
export function useStoreGroupsListQuery(options?: ListOptions, enabled = true) {
  const { storeGroups } = useServices();

  return useApiQuery({
    queryKey: [Module.StoreGroups, options?.filters, options?.limit],
    queryFn: () => storeGroups.list(options),
    networkMode: "offlineFirst",
    enabled,
  });
}

/**
 * Fetch all store groups for a project
 */
export function useStoreGroupsQuery(projectId: string, options?: ListOptions) {
  const { storeGroups } = useServices();

  return useApiQuery({
    queryKey: [Module.StoreGroups, projectId, options?.filters, options?.limit],
    queryFn: () => storeGroups.listByProject(projectId, options),
    networkMode: "offlineFirst",
  });
}

/**
 * Fetch a single store group by ID
 */
export function useStoreGroupQuery(groupId: string, enabled = true) {
  const { storeGroups } = useServices();

  return useApiQuery({
    queryKey: [Module.StoreGroups, groupId],
    queryFn: () => storeGroups.getStoreGroup(groupId),
    networkMode: "offlineFirst",
    enabled: enabled && Boolean(groupId),
  });
}

/**
 * Fetch stores within a store group (infinite query support)
 */
export function useStoreGroupMembersQuery(
  groupId: string,
  options?: ListOptions,
) {
  const { storeGroups } = useServices();

  // Return infinite query structure compatible with legacy hook
  return useInfiniteQuery<
    StoreGroupMembersPage,
    Error,
    InfiniteData<StoreGroupMembersPage>,
    StoreGroupMembersQueryKey,
    string | undefined
  >({
    queryKey: [
      "storeGroupMembers",
      groupId,
      options?.filters,
      options?.limit,
      options?.sorting,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await storeGroups.listStoresInGroup(groupId, {
        ...options,
        lastItem: pageParam,
      });

      return {
        items: response.items,
        count: response.count ?? response.items.length,
        last_item: response.lastItem,
      };
    },
    staleTime: 3 * 60 * 1000, // 3 minutes (members change more frequently)
    enabled: Boolean(groupId),
    initialPageParam: undefined,
    getNextPageParam: ({ last_item }) => last_item || undefined,
  });
}

/**
 * Create a new store group
 */
export function useCreateStoreGroupMutation(
  options?: MutationOptions<ReadStoreGroup, WriteStoreGroup>,
) {
  const { storeGroups } = useServices();
  const queryClient = useQueryClient();

  return useMutationWithErrorHandling<
    ReadStoreGroup,
    Error,
    WriteStoreGroup,
    unknown
  >({
    ...options,
    mutationFn: (data: WriteStoreGroup) => storeGroups.createStoreGroup(data),
    onSuccess: (data, variables, context) => {
      // Invalidate list queries since a new group was created
      queryClient.invalidateQueries({
        queryKey: [Module.StoreGroups],
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Update store group details (name, description)
 */
export function useUpdateStoreGroupMutation(
  groupId: string,
  options?: MutationOptions<ReadStoreGroup, WriteStoreGroup>,
) {
  const { storeGroups } = useServices();
  const queryClient = useQueryClient();

  return useMutationWithErrorHandling<
    ReadStoreGroup,
    Error,
    WriteStoreGroup,
    unknown
  >({
    ...options,
    mutationFn: (data: WriteStoreGroup) =>
      storeGroups.updateStoreGroup(groupId, data),
    onSuccess: (updated, variables, context) => {
      // Update cached store group
      queryClient.setQueryData([Module.StoreGroups, groupId], updated);
      // Invalidate list queries
      queryClient.invalidateQueries({
        queryKey: [Module.StoreGroups],
      });

      options?.onSuccess?.(updated, variables, context);
    },
  });
}

/**
 * Update stores in a store group (add/remove stores)
 * NOTE: Takes full WriteStoreGroup object since /stores endpoint expects it
 */
export function useUpdateStoreGroupStoresMutation(
  groupId: string,
  options?: MutationOptions<ReadStoreGroup, WriteStoreGroup>,
) {
  const { storeGroups } = useServices();
  const queryClient = useQueryClient();

  return useMutationWithErrorHandling<
    ReadStoreGroup,
    Error,
    WriteStoreGroup,
    unknown
  >({
    ...options,
    mutationFn: (data: WriteStoreGroup) =>
      storeGroups.updateGroupStores(groupId, data),
    onSuccess: (data, variables, context) => {
      // Invalidate members list to fetch fresh data
      queryClient.invalidateQueries({
        queryKey: ["storeGroupMembers", groupId],
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
}

/**
 * Delete a store group
 */
export function useDeleteStoreGroupMutation(
  groupId: string,
  options?: MutationOptions<undefined, unknown>,
) {
  const { storeGroups } = useServices();
  const queryClient = useQueryClient();

  return useMutationWithErrorHandling<undefined, Error, unknown, unknown>({
    ...options,
    mutationFn: async () => {
      await storeGroups.deleteStoreGroup(groupId);
      return undefined;
    },
    onSuccess: (data, variables, context) => {
      // Invalidate all store group queries
      queryClient.invalidateQueries({
        queryKey: [Module.StoreGroups],
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
}
